import React, { useState, useEffect } from 'react';
import { Bell, BellRing } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { notificationService } from '@/services/notificationService';
import { soundNotificationService } from '@/services/soundNotificationService';
import { useAuth } from '@/context/AuthContext';
import NotificationsPanel from './NotificationsPanel';

interface NotificationBellProps {
  size?: 'sm' | 'md';
}

const NotificationBell: React.FC<NotificationBellProps> = ({ size = 'md' }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const { currentUser } = useAuth();

  useEffect(() => {
    if (!currentUser?.email) return;

    // Set up real-time listener for unread count
    const unsubscribe = notificationService.listenToUnreadCount(
      currentUser.email,
      (count: number) => {
        const previousCount = unreadCount;
        setUnreadCount(count);
        
        // Animate bell and play sound if new notifications arrived
        if (count > previousCount && count > 0) {
          setIsAnimating(true);
          setTimeout(() => setIsAnimating(false), 1000);
          
          // Play notification sound with user's preference
          soundNotificationService.playNotificationSound(undefined, currentUser?.uid);
        }
      }
    );

    return () => unsubscribe();
  }, [currentUser?.email]);

  const fetchUnreadCount = async () => {
    if (!currentUser?.email) return;
    
    try {
      const count = await notificationService.getUnreadCount(currentUser.email);
      const previousCount = unreadCount;
      setUnreadCount(count);
      
      // Animate bell and play sound if new notifications arrived
      if (count > previousCount && count > 0) {
        setIsAnimating(true);
        setTimeout(() => setIsAnimating(false), 1000);
        
        // Play notification sound with user's preference
        soundNotificationService.playNotificationSound(undefined, currentUser?.uid);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  // Expose refresh method globally for other components to use
  useEffect(() => {
    if (currentUser?.email) {
      (window as any).refreshNotificationCount = fetchUnreadCount;
    }
    
    return () => {
      if ((window as any).refreshNotificationCount === fetchUnreadCount) {
        delete (window as any).refreshNotificationCount;
      }
    };
  }, [currentUser?.email, fetchUnreadCount]);

  const handleClick = () => {
    setIsOpen(true);
    // Refresh notifications when opening
    fetchUnreadCount();
  };

  const handleClose = () => {
    setIsOpen(false);
    // Refresh count after closing to update any read notifications
    setTimeout(fetchUnreadCount, 500);
  };

  const iconSize = size === 'sm' ? 'h-3.5 w-3.5' : 'h-5 w-5';

  return (
    <>
      <motion.button
        onClick={handleClick}
        className="relative flex-shrink-0"
        whileTap={{ scale: 0.9 }}
        animate={isAnimating ? {
          rotate: [0, -12, 12, -12, 12, 0],
          scale: [1, 1.15, 1]
        } : {}}
        transition={{ duration: 0.5 }}
      >
        {unreadCount > 0 ? (
          <BellRing className={`${iconSize} text-white drop-shadow-[0_0_4px_rgba(255,255,255,0.5)]`} />
        ) : (
          <Bell className={`${iconSize} text-white hover:text-gray-200 transition-colors`} />
        )}
        
        <AnimatePresence>
          {unreadCount > 0 && (
            <motion.span
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0, opacity: 0 }}
              className={`absolute -top-1.5 -right-2 ${size === 'sm' ? 'min-w-[14px] h-[14px] text-[9px]' : 'min-w-[16px] h-4 text-[10px]'} bg-[#f1e8d2] text-[#5a3a1a] font-bold rounded-full flex items-center justify-center px-1`}
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </motion.span>
          )}
        </AnimatePresence>
      </motion.button>

      <NotificationsPanel 
        isOpen={isOpen}
        onClose={handleClose}
      />
    </>
  );
};

export default NotificationBell;
